import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  DirectionsCar,
  Speed,
  LocalGasStation,
  Build,
  Warning,
  CheckCircle,
  ExpandMore,
  Refresh
} from '@mui/icons-material';
import apiClient from '../api';

const VehicleDetailsPanel = ({ vehicleId, vehicleData: initialVehicleData, onClose }) => {
  const [vehicle, setVehicle] = useState(initialVehicleData || null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!initialVehicleData);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [locationCity, setLocationCity] = useState(null);
  const [loadingCity, setLoadingCity] = useState(false);

  useEffect(() => {
    // Only load data if we don't have initialVehicleData
    // If we have initialVehicleData, it's already set in state and we don't need to fetch
    // Check if initialVehicleData has actual content (not just an empty object)
    const hasInitialData = initialVehicleData && 
      (initialVehicleData.id || initialVehicleData.name || initialVehicleData.vin);
    
    if (vehicleId && !hasInitialData) {
      console.log('[VehicleDetailsPanel] No initial data, fetching from API for vehicle:', vehicleId);
      loadVehicleData();
    } else if (hasInitialData && vehicleId && !stats) {
      // We have initial data, just fetch stats in the background
      console.log('[VehicleDetailsPanel] Using initial data, fetching stats for vehicle:', vehicleId);
      const fetchStats = async () => {
        try {
          const dateRange = getDateRange();
          const statsResponse = await apiClient.get(`/samsara/vehicles/${vehicleId}/stats?startTime=${dateRange.start}&endTime=${dateRange.end}`);
          const statsData = statsResponse.data;
          if (statsData && 
              (!statsData.fuelUsed || statsData.fuelUsed.value === 0) &&
              (!statsData.distance || statsData.distance.value === 0) &&
              (!statsData.engineHours || statsData.engineHours.value === 0)) {
            setStats(null);
          } else {
            setStats(statsData);
          }
        } catch (statsErr) {
          console.warn('Failed to load vehicle stats:', statsErr.message);
          setStats(null);
        }
      };
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  // Update vehicle data if initialVehicleData changes
  useEffect(() => {
    if (initialVehicleData) {
      console.log('[VehicleDetailsPanel] Setting vehicle from initialVehicleData:', {
        id: initialVehicleData.id,
        name: initialVehicleData.name,
        vin: initialVehicleData.vin,
        make: initialVehicleData.make,
        model: initialVehicleData.model,
        year: initialVehicleData.year
      });
      setVehicle(initialVehicleData);
      setLoading(false);
    }
  }, [initialVehicleData]);

  const loadVehicleData = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we already have vehicle data from props, use it and skip API call
      if (initialVehicleData) {
        setVehicle(initialVehicleData);
        setLoading(false);
        
        // Only fetch stats (vehicle data is already available)
        if (vehicleId) {
          try {
            const statsResponse = await apiClient.get(`/samsara/vehicles/${vehicleId}/stats?startTime=${getDateRange().start}&endTime=${getDateRange().end}`);
            const statsData = statsResponse.data;
            // Check if stats are empty (all zeros) - this means the endpoint is not available
            if (statsData && 
                (!statsData.fuelUsed || statsData.fuelUsed.value === 0) &&
                (!statsData.distance || statsData.distance.value === 0) &&
                (!statsData.engineHours || statsData.engineHours.value === 0)) {
              // Stats endpoint not available, but don't set error - just don't show stats section
              setStats(null);
            } else {
              setStats(statsData);
            }
          } catch (statsErr) {
            // Stats failed to load, but this is optional data so we just don't show it
            console.warn('Failed to load vehicle stats:', statsErr.message);
            setStats(null);
          }
        }
        return;
      }

      // If we don't have initial data, fetch from API
      if (!vehicleId) {
        setError('No vehicle ID provided');
        setLoading(false);
        return;
      }

      // Load vehicle details and stats in parallel
      const [vehicleResponse, statsResponse] = await Promise.allSettled([
        apiClient.get(`/samsara/vehicles/${vehicleId}`),
        apiClient.get(`/samsara/vehicles/${vehicleId}/stats?startTime=${getDateRange().start}&endTime=${getDateRange().end}`)
      ]);

      if (vehicleResponse.status === 'fulfilled') {
        const vehicleData = vehicleResponse.value.data;
        // Only set vehicle if we got actual data (not empty object)
        if (vehicleData && Object.keys(vehicleData).length > 0) {
          setVehicle(vehicleData);
        } else {
          // If API returns empty, but we have initial data, use that
          if (initialVehicleData) {
            setVehicle(initialVehicleData);
          } else {
            setError('Vehicle data not available');
          }
        }
      } else {
        console.warn('Failed to load vehicle details:', vehicleResponse.reason);
        // If we have initial data, still use it
        if (initialVehicleData) {
          setVehicle(initialVehicleData);
        } else {
          setError('Failed to load vehicle data');
        }
      }

      if (statsResponse.status === 'fulfilled') {
        const statsData = statsResponse.value.data;
        // Check if stats are empty (all zeros) - this means the endpoint is not available
        if (statsData && 
            (!statsData.fuelUsed || statsData.fuelUsed.value === 0) &&
            (!statsData.distance || statsData.distance.value === 0) &&
            (!statsData.engineHours || statsData.engineHours.value === 0)) {
          // Stats endpoint not available, but don't set error - just don't show stats section
          setStats(null);
        } else {
          setStats(statsData);
        }
      } else {
        // Stats failed to load, but this is optional data so we just don't show it
        setStats(null);
      }
    } catch (err) {
      console.error('Error loading vehicle data:', err);
      // If we have initial data, still use it even if API fails
      if (initialVehicleData) {
        setVehicle(initialVehicleData);
      } else {
        setError('Failed to load vehicle data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30); // Last 30 days
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadVehicleData();
    setRefreshing(false);
  };

  // Log vehicle data when rendering to debug
  useEffect(() => {
    if (vehicle) {
      console.log('[VehicleDetailsPanel] Rendering with vehicle data:', {
        id: vehicle.id,
        name: vehicle.name,
        vin: vehicle.vin,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year
      });
    }
  }, [vehicle]);

  // Fetch city name from coordinates when location is available
  useEffect(() => {
    const fetchCityName = async () => {
      if (vehicle?.location?.latitude && vehicle?.location?.longitude) {
        setLoadingCity(true);
        try {
          const response = await apiClient.get('/googlemaps/reverse-geocode', {
            params: {
              lat: vehicle.location.latitude,
              lng: vehicle.location.longitude
            }
          });
          if (response.data.success && response.data.location) {
            setLocationCity(response.data.location);
          }
        } catch (err) {
          console.warn('Failed to fetch city name:', err.message);
          // Fallback to lat/long if reverse geocoding fails
          setLocationCity(null);
        } finally {
          setLoadingCity(false);
        }
      } else {
        setLocationCity(null);
      }
    };

    fetchCityName();
  }, [vehicle?.location?.latitude, vehicle?.location?.longitude]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!vehicle) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No vehicle data available
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'online':
        return 'success';
      case 'idle':
        return 'warning';
      case 'offline':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            <DirectionsCar sx={{ mr: 1, verticalAlign: 'middle' }} />
            {vehicle.name || vehicle.vehicleName || `Vehicle ${vehicle.id}`}
          </Typography>
          <Box>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <Refresh />
              </IconButton>
            </Tooltip>
            {onClose && (
              <Button onClick={onClose} size="small">
                Close
              </Button>
            )}
          </Box>
        </Box>

        {/* Vehicle Status */}
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="textSecondary">
                  Status:
                </Typography>
                <Chip
                  label={vehicle.status || 'Unknown'}
                  color={getStatusColor(vehicle.status)}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="textSecondary">
                  Last Update:
                </Typography>
                <Typography variant="body2">
                  {formatDate(vehicle.lastSeen || vehicle.updatedAt)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Vehicle Information */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">Vehicle Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  VIN: {vehicle.vin || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Make: {vehicle.make || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Model: {vehicle.model || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Year: {vehicle.year || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Current Location */}
        {vehicle.location && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Current Location</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Location: {loadingCity ? (
                      <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
                    ) : locationCity ? (
                      <strong>{locationCity}</strong>
                    ) : (
                      `${vehicle.location.latitude?.toFixed(6) || 'N/A'}, ${vehicle.location.longitude?.toFixed(6) || 'N/A'}`
                    )}
                  </Typography>
                  {locationCity && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                      Coordinates: {vehicle.location.latitude?.toFixed(6)}, {vehicle.location.longitude?.toFixed(6)}
                    </Typography>
                  )}
                </Grid>
                {vehicle.location.speed && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Speed />
                      <Typography variant="body2">
                        Speed: {vehicle.location.speed} mph
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {vehicle.location.heading && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Heading: {vehicle.location.heading}°
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Vehicle Statistics */}
        {stats && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">Performance Statistics (Last 30 Days)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <LocalGasStation color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {formatNumber(stats.fuelUsed?.value)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.fuelUsed?.unit || 'gallons'} Fuel Used
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Speed color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {formatNumber(stats.distance?.value)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.distance?.unit || 'miles'} Distance
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Build color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {formatNumber(stats.engineHours?.value)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.engineHours?.unit || 'hours'} Engine Hours
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary">
                      {formatNumber(stats.idleTime?.value)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stats.idleTime?.unit || 'hours'} Idle Time
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Maintenance Alerts */}
        {vehicle.maintenance && vehicle.maintenance.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">
                Maintenance Alerts ({vehicle.maintenance.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Priority</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicle.maintenance.map((alert, index) => (
                      <TableRow key={index}>
                        <TableCell>{alert.type || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={alert.status || 'Unknown'}
                            color={alert.status === 'overdue' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(alert.dueDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={alert.priority || 'Medium'}
                            color={alert.priority === 'high' ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Safety Events */}
        {vehicle.safetyEvents && vehicle.safetyEvents.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1">
                Recent Safety Events ({vehicle.safetyEvents.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicle.safetyEvents.slice(0, 5).map((event, index) => (
                      <TableRow key={index}>
                        <TableCell>{event.type || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={event.severity || 'Unknown'}
                            color={event.severity === 'high' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(event.timestamp)}</TableCell>
                        <TableCell>
                          {event.location ? 
                            `${event.location.latitude?.toFixed(4)}, ${event.location.longitude?.toFixed(4)}` : 
                            'N/A'
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* No Samsara Connection Message */}
        {!vehicle && !loading && !error && (
          <Alert severity="info">
            Configure Samsara integration to view detailed vehicle information.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default VehicleDetailsPanel;
